import { http } from '@/shared/api';
import type { ApiResponse } from '@/shared/api';
import type { PointBalance } from '@/shared/model/point-balance';
import { setSpaceInfo } from '@/shared/config/space-store';

export interface ServerProjectSavePayload {
  projectId?: string | number;
  name?: string;
  cover?: string;
  description?: string;
  canvasData?: string;
  extraData?: string;
  copyFrom?: string | number;
}

export interface ProjectServerSaveResult {
  projectId: string;
}

/**
 * 保存/创建项目到服务器
 */
export async function requestProjectServerSave(payload: ServerProjectSavePayload): Promise<ApiResponse<ProjectServerSaveResult>> {
  const response = await http.post<Record<string, unknown>>('/zs/AiCanvasProject/saveProject', {
    ...(payload.projectId ? { project_id: payload.projectId } : {}),
    ...(payload.name !== undefined ? { name: payload.name || '未命名项目' } : {}),
    ...(payload.cover !== undefined ? { cover: payload.cover } : {}),
    ...(payload.description !== undefined ? { description: payload.description } : {}),
    ...(payload.canvasData !== undefined ? { canvas_data: payload.canvasData } : {}),
    ...(payload.extraData !== undefined ? { extra_data: payload.extraData } : {}),
    ...(payload.copyFrom ? { copyfrom: payload.copyFrom } : {}),
  });

  if (response.code !== 1 || !response.data) {
    return {
      code: response.code,
      msg: response.msg || '保存失败',
    };
  }

  // 统一解析返回的 ID
  const rawId = response.data.project_id ?? response.data.projectId ?? response.data.id;
  const projectId = String(rawId);

  if (!projectId) {
    return {
      code: -1,
      msg: '项目保存成功，但未返回服务端项目ID',
    };
  }

  return {
    code: 1,
    msg: response.msg || '保存成功',
    data: {
      projectId,
    },
  };
}

export interface ServerProjectUpdatePayload {
  projectId: string | number;
  name?: string;
  cover?: string;
  description?: string;
}

export interface ServerProjectDetailResult {
  permission: string;//readonly,editable
  copy_point?: number;
  project: {
    id: string,
    uid: string,
    team_id?: string,
    name: string,
    cover: string,
    cover_width: number,
    cover_height: number,
    description: string,
    canvas_data: unknown[],
    snapshot_data: unknown,
    extra_data: unknown,
    copyfrom: number,
    create_time: number,
    update_time: number,
    team_expire_time: number
  },
  current_team?: {
    token?: string,
    team_id?: string,
    team_name?: string,
    team_kind?: string,
    current_plan_type?: string,
    plan_name?: string,
    plan_status?: string,
    concurrency_limit?: number,
    is_personal_team?: number,
    collaboration_enabled?: number,
    member_role?: string,
    grace_expire_time?: number,
    seat_status?: string,
    has_valid_seat?: number,
    enter_mode?: string,
    ai_watermark?: number,
    point_limit_month?: number,
    point_used_month?: number,
    point_remain_month?: number,
    team_point_limit_month?: number,
    point_balance_permanent?: number,
    point_balance?: PointBalance,
  }
}

/**
 * 更新项目
 */
export async function requestProjectServerUpdate(payload: ServerProjectUpdatePayload): Promise<ApiResponse<Record<string, unknown>>> {
  return http.post<Record<string, unknown>>('/zs/AiCanvasProject/updateProject', {
    project_id: payload.projectId,
    ...(payload.name !== undefined ? { name: payload.name } : {}),
    ...(payload.cover !== undefined ? { cover: payload.cover } : {}),
    ...(payload.description !== undefined ? { description: payload.description } : {}),
  });
}

/** 删除项目 */
export async function requestProjectServerDelete(projectId: string | number): Promise<ApiResponse<Record<string, unknown>>> {
  return http.post<Record<string, unknown>>('/zs/AiCanvasProject/deleteProject', {
    project_id: projectId,
  });
}

/** 获取项目详情，用于校验项目是否存在 */
export function requestProjectDetail(project_id: string, share_id?: string, community_id?: string) {
  return http.post<ServerProjectDetailResult>('/zs/AiCanvasProject/getProjectDetail', { project_id, share_id, community_id }).then(res => {
    if (res.code === 1 && (res.data as any)?.space) {
      setSpaceInfo((res.data as any).space);
    }
    return res;
  });
}

/** 申请编辑权限 */
export function requestApplyEditPermission(project_id: string) {
  return http.post('/zs/AiCanvasProject/applyEditPermission', { project_id })
}

/** 复刻项目 */
export function requestClonePublicProject(param: { share_id?: string, community_id?: string }) {
  return http.post<{ url: string }>('/zs/AiCanvasProject/clonePublicProject', param, { timeout: 30000 })
}

export interface YjsAuthTokenData {
  token: string;
  mode: string;
  updateInfo?: Record<string, unknown>;
  canvas_version?: {
    version?: number;
    need_update?: number;
    update_info?: string;
  };
}

/** 获取协同编辑 Token */
export async function requestYjsAuthToken(projectId: string, share_id?: string, community_id?: string, version?: number) {
  return http.post<YjsAuthTokenData>('/zs/User/getCollabToken', {
    project_id: projectId,
    share_id,
    community_id,
    ...(version !== undefined ? { version } : {})
  });
}
